import React, { FC } from 'react';
import { useRouter } from 'next/router';
import { Modal, Image, Button, Heading, Text, Types, baseVariables } from '@marriott/mi-ui-library';
import { useMediaQuery } from '../../../hooks';
import {
  bookNow,
  getImageType,
  getPropertyImages,
  moveDecimalPoint,
  getBookNowPayloadForRfp,
  formatAmount,
} from '../../../utils';
import { useLocaleStore, useRfpStore, useQuickGroupStore } from '../../../store';
import { ViewType } from '../../SearchResults/SearchResults.types';
import { StyledDivider, StyledQuickGroupInterstitial } from './QuickGroupInterstitial.styles';
import { QuickGroupInterstitialProps } from './QuickGroupInterstitial.types';

export const QuickGroupInterstitial: FC<QuickGroupInterstitialProps> = props => {
  const { labels, data, show, onCloseModal } = props;
  const { quickGroup, rfp, ctas } = labels;
  const { node, groupRate } = data ?? {};
  const { basicInformation, media } = node;
  const { roomRates, eventSpaceRates } = groupRate;

  const router = useRouter();

  const isTablet = useMediaQuery(baseVariables.mediaQuery.md);
  const isDesktop = useMediaQuery(baseVariables.mediaQuery.lg);

  const { locale } = useLocaleStore();
  const { eventProfileData } = useRfpStore();

  const imageType = getImageType(ViewType.LIST_VIEW, isDesktop, isTablet, false);
  const image = getPropertyImages(imageType, media?.primaryImage)[0];

  const handleBookNow = async () => {
    try {
      const bookNowPayload = getBookNowPayloadForRfp(data, eventProfileData, locale);
      const response = await bookNow(ctas.bookNowUrl, bookNowPayload);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const url = new URL(response.url);
      url.searchParams.append('brandCode', bookNowPayload.brandCode);
      useQuickGroupStore.persist.clearStorage();
      window.location.href = url.toString();
    } catch (error) {
      console.error('Error: ', error);
    }
  };

  return (
    <StyledQuickGroupInterstitial>
      <Modal
        modalId="quickGroupInterstitial"
        role="dialog"
        labelledBy="quickGroupInterstitial"
        secondaryClassName="my-md-3 qg-interstitial"
        show={show}
        popupOpenState={show}
        handleBlur={true}
        disableScrollOnBody={true}
        setPopupOpenState={onCloseModal}
        onClose={onCloseModal}
      >
        <Modal.Header
          labelText={basicInformation.name}
          customHeadingClass="t-subtitle-xl"
          popupHeaderOnCLoseFunc={onCloseModal}
        />
        <Modal.Body className="custom-scrollbar p-0 interstitial-body">
          <Image defaultImageURL={image?.defaultImageUrl} altText={image?.altText} customClass="property-image" />
          <div className="p-4">
            <Heading
              variation={Types.headingType.subtitle}
              titleText={quickGroup.title}
              element={Types.tags.h5}
              fontSize={isTablet ? Types.size.extraLarge : Types.size.medium}
            />
            <Text
              element={Types.tags.div}
              copyText={quickGroup.description}
              fontSize={Types.size.small}
              customClass="pb-4"
            />
            {quickGroup.instructions.map((instruction, index) => (
              <Text
                key={`quick-group-${index}`}
                element={Types.tags.div}
                copyText={instruction}
                fontSize={Types.size.extraSmall}
                customClass="pb-2"
              />
            ))}
            <div className="d-flex pt-3 pricing-row">
              <div className="d-flex pb-4 pb-md-0">
                {roomRates?.[0]?.amount?.origin?.value && (
                  <div>
                    <div className="t-font-xs">{quickGroup.guestRoomsFrom}</div>
                    <span className="t-subtitle-xl">
                      {formatAmount(
                        moveDecimalPoint(
                          roomRates[0].amount.origin.value,
                          roomRates[0].amount.origin?.valueDecimalPoint
                        ) || 0
                      )}
                    </span>
                    <span className="t-font-xs currency">
                      {' '}
                      {roomRates[0].amount.origin?.currency}
                      {quickGroup.perNight}
                    </span>
                  </div>
                )}
                {eventSpaceRates?.[0]?.amount?.origin?.value && (
                  <div className="ml-5">
                    <div className="t-font-xs">{quickGroup.eventRoomsFrom}</div>
                    <span className="t-subtitle-xl">
                      {formatAmount(
                        moveDecimalPoint(
                          eventSpaceRates[0].amount.origin.value,
                          eventSpaceRates[0].amount.origin?.valueDecimalPoint
                        ) || 0
                      )}
                    </span>
                    <span className="t-font-xs currency">
                      {' '}
                      {eventSpaceRates[0].amount.origin?.currency}
                      {quickGroup.perDay}
                    </span>
                  </div>
                )}
              </div>
              <Button
                buttonCopy={ctas.bookNow}
                ariaLabel={ctas.bookNowAriaLabel}
                className="m-button-secondary m-button-s"
                callback={handleBookNow}
              />
            </div>
            <StyledDivider />
            <Heading
              variation={Types.headingType.subtitle}
              titleText={rfp.title}
              element={Types.tags.h5}
              fontSize={isTablet ? Types.size.extraLarge : Types.size.medium}
            />
            <Text element={Types.tags.div} copyText={rfp.description} fontSize={Types.size.small} customClass="pb-4" />
            {rfp.instructions.map((instruction, index) => (
              <Text
                key={`rfp-${index}`}
                element={Types.tags.div}
                copyText={instruction}
                fontSize={Types.size.extraSmall}
                customClass="pb-2"
              />
            ))}
            <div className="add-to-rfp-cta">
              <Button
                buttonCopy={ctas.addToRfp}
                ariaLabel={ctas.addToRfpAriaLabel}
                className="m-button-primary m-button-s mt-3"
                callback={() => router.push(ctas.addToRfpUrl)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.FooterGeneric className="interstitial-footer" />
      </Modal>
    </StyledQuickGroupInterstitial>
  );
};
